import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Quote, Star, ExternalLink, MessageSquarePlus } from "lucide-react";
import { Reveal } from "@/components/ui/reveal";
import { supabase } from "@/integrations/supabase/client";

/**
 * Avis Google réels.
 * Les avis sont récupérés depuis la fiche Google Business (Places API) via
 * l'edge function `google-reviews`. Tant qu'aucun avis n'est publié, un état
 * vide invitant à laisser un avis est affiché — aucun faux témoignage.
 */

type GoogleReview = {
  author: string;
  rating: number;
  text: string;
  relativeTime?: string;
  profilePhoto?: string;
};

/** Lien direct pour laisser un avis sur la fiche Google "Regard de Nature - Laura ELIE". */
const GOOGLE_REVIEWS_URL =
  "https://search.google.com/local/writereview?placeid=ChIJfeeMFirtrhIRPosnsaKjSKU";

export const TestimonialsSection: React.FC = () => {
  const [reviews, setReviews] = useState<GoogleReview[]>([]);
  const [rating, setRating] = useState<number | null>(null);
  const [ratingCount, setRatingCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("google-reviews");
        if (!active) return;
        if (!error && data && Array.isArray(data.reviews)) {
          setReviews(data.reviews as GoogleReview[]);
          setRating(typeof data.rating === "number" ? data.rating : null);
          setRatingCount(
            typeof data.userRatingCount === "number" ? data.userRatingCount : null
          );
        }
      } catch {
        // silencieux : on affiche simplement l'état vide
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Balisage AggregateRating (affiché uniquement quand de vrais avis existent)
  const aggregateJsonLd =
    reviews.length > 0 && rating && ratingCount
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Regard de Nature - Laura ELIE",
          url: "https://www.regard-de-nature.fr",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: Number(rating.toFixed(1)),
            reviewCount: ratingCount,
            bestRating: 5,
            worstRating: 1,
          },
          review: reviews.slice(0, 5).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.author },
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
            reviewBody: r.text,
          })),
        }
      : null;


  return (
    <section
      id="temoignages"
      className="py-12 sm:py-16 md:py-20 bg-background"
      aria-labelledby="temoignages-heading"
    >
      <div className="container mx-auto px-3 sm:px-4">
        {aggregateJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(aggregateJsonLd) }}
          />
        )}
        <Reveal className="text-center mb-8 sm:mb-12">
          <h2
            id="temoignages-heading"
            className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary mb-3"
          >
            Ils m'ont fait confiance
          </h2>
          <div className="w-20 h-px bg-secondary mx-auto mb-4" />
          <p className="text-base sm:text-lg text-foreground/80 max-w-2xl mx-auto font-montserrat">
            Les avis publiés sur ma fiche Google
          </p>
          {aggregateJsonLd && rating && ratingCount && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="flex gap-0.5" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star
                    key={s}
                    className={
                      s < Math.round(rating)
                        ? "w-4 h-4 text-secondary fill-current"
                        : "w-4 h-4 text-muted-foreground/40"
                    }
                  />
                ))}
              </div>
              <span className="text-sm font-montserrat text-foreground/70">
                {rating.toFixed(1)}/5 · {ratingCount} avis Google
              </span>
            </div>
          )}
        </Reveal>


        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="border-border/50">
                <CardContent className="p-6 space-y-3">
                  <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-full bg-muted rounded animate-pulse" />
                  <div className="h-3 w-5/6 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-2/3 bg-muted rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : reviews.length > 0 ? (
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto items-stretch">
            {reviews.slice(0, 6).map((t, i) => (
              <Reveal as="li" key={t.author + i} delay={i * 120} className="h-full">
                <Card className="h-full border-border/50 hover:shadow-elegant transition-shadow duration-300">
                  <CardContent className="p-6 flex flex-col h-full">
                    <Quote className="w-7 h-7 text-secondary mb-3" aria-hidden="true" />
                    <div
                      className="flex gap-0.5 mb-3"
                      aria-label={`${t.rating} étoiles sur 5`}
                    >
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star
                          key={s}
                          className={
                            s < Math.round(t.rating)
                              ? "w-4 h-4 text-secondary fill-current"
                              : "w-4 h-4 text-muted-foreground/40"
                          }
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                    <p className="text-sm sm:text-base text-foreground/80 font-montserrat leading-relaxed italic mb-4">
                      « {t.text} »
                    </p>
                    <div className="mt-auto">
                      <p className="font-cinzel text-primary font-semibold">{t.author}</p>
                      {t.relativeTime && (
                        <p className="text-xs font-montserrat uppercase tracking-wide text-secondary">
                          {t.relativeTime}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </ul>
        ) : (
          <Reveal className="max-w-2xl mx-auto">
            <Card className="border-secondary/30 bg-secondary/5">
              <CardContent className="p-6 sm:p-10 text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-secondary/15 flex items-center justify-center mx-auto">
                  <MessageSquarePlus className="w-6 h-6 text-secondary" aria-hidden="true" />
                </div>
                <p className="text-base sm:text-lg text-foreground/80 font-montserrat leading-relaxed">
                  Les premiers avis Google arrivent bientôt.
                  <br />
                  Vous avez été accompagné(e)&nbsp;? Votre retour aidera d'autres
                  personnes à franchir le pas.
                </p>
                <Button
                  asChild
                  className="bg-gradient-primary text-primary-foreground hover:opacity-90 font-montserrat"
                >
                  <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Laisser un avis Google
                  </a>
                </Button>
              </CardContent>
            </Card>
          </Reveal>
        )}

        {reviews.length > 0 && (
          <Reveal className="text-center mt-8">
            <Button
              asChild
              variant="outline"
              className="font-montserrat border-primary/40 text-primary hover:bg-primary/10"
            >
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Voir tous les avis Google
              </a>
            </Button>
          </Reveal>
        )}
      </div>
    </section>
  );
};
